import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FolderKanban, LayoutGrid, LogOut, Menu, Settings, Users } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { getToken, setToken, setUnauthorizedHandler } from "@/lib/adminApi";
import { AdminLogin } from "@/components/admin/AdminLogin";
import "@/styles/hausa-vendor.css";
import "@/styles/studio.css";

const NAV = [
  ["/admin/studio", "Dashboard", LayoutGrid, true],
  ["/admin/studio/clients", "Clients", Users],
  ["/admin/studio/projects", "Projects", FolderKanban],
  ["/admin/studio/settings", "Settings", Settings],
];

const Brand = () => (
  <Link to="/admin/studio" className="st-brand" data-testid="studio-brand-link">
    <span className="st-brand__name">haus<b>A</b></span>
    <span className="st-brand__sub">Interior Design Studio — by Flex Living</span>
  </Link>
);

const NavLinks = ({ prefix }) => (
  <nav className="st-nav">
    {NAV.map(([to, label, Icon, end]) => (
      <NavLink key={to} to={to} end={end} data-testid={`${prefix}-nav-${label.toLowerCase()}`}>
        <Icon size={17} strokeWidth={1.6} /> {label}
      </NavLink>
    ))}
  </nav>
);

const Foot = ({ onLogout }) => (
  <div className="st-side__foot">
    <Link to="/admin" data-testid="studio-back-to-admin-link"><ArrowLeft size={14} /> Back to admin</Link>
    <button onClick={onLogout} data-testid="studio-logout-button"><LogOut size={14} /> Sign out</button>
  </div>
);

const MobileBar = ({ onLogout }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  return (
    <div className="st-topbar" data-testid="studio-topbar">
      <Brand />
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button className="st-icon-btn" aria-label="Open menu" data-testid="studio-menu-button"><Menu size={22} /></button>
        </SheetTrigger>
        <SheetContent side="left" className="st st-drawer w-[280px]">
          <SheetTitle className="sr-only">Studio menu</SheetTitle>
          <Brand />
          <NavLinks prefix="studio-drawer" />
          <Foot onLogout={onLogout} />
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default function StudioLayout() {
  const [token, setTok] = useState(getToken());
  const logout = () => { setToken(null); setTok(null); };

  useEffect(() => {
    document.title = "Studio | hausA Interior Design Studio";
    setUnauthorizedHandler(() => {
      if (getToken()) toast.error("Session expired. Please sign in again.");
      logout();
    });
  }, []);

  if (!token) {
    return (
      <>
        <AdminLogin onLogin={(t) => { setToken(t); setTok(t); }} />
        <Toaster position="bottom-right" />
      </>
    );
  }

  return (
    <div className="st" data-testid="studio-app">
      <aside className="st-side" data-testid="studio-sidebar">
        <Brand />
        <NavLinks prefix="studio" />
        <Foot onLogout={logout} />
      </aside>
      <MobileBar onLogout={logout} />
      <main className="st-main"><Outlet /></main>
      <Toaster position="bottom-right" />
    </div>
  );
}
